import React, { useCallback, useEffect, useState } from 'react';
import { AlertOctagon, RefreshCw, Loader2, ShieldOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' });
};

const BADGES = {
  'email.bounced': { label: 'Bounce', tone: 'bg-red-500/15 text-red-300 border-red-500/40' },
  'email.complained': { label: 'Spam complaint', tone: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/40' },
  'email.delivered': { label: 'Delivered', tone: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  'email.opened': { label: 'Opened', tone: 'bg-blue-500/15 text-blue-300 border-blue-500/40' },
  'email.clicked': { label: 'Clicked', tone: 'bg-blue-500/15 text-blue-300 border-blue-500/40' },
  'email.delivery_delayed': { label: 'Delayed', tone: 'bg-zinc-700 text-gray-300 border-zinc-600' },
  'email.sent': { label: 'Sent', tone: 'bg-zinc-800 text-gray-400 border-zinc-700' },
};

// Filter definitions used by the chip row below the header. Each entry
// returns true for rows that should be shown.
const FILTERS = [
  { id: 'all', label: 'All', match: () => true },
  { id: 'hard', label: 'Hard bounce', match: (r) => r.event_type === 'email.bounced' && (r.bounce?.type || '').toLowerCase().startsWith('perm') },
  { id: 'soft', label: 'Soft bounce', match: (r) => r.event_type === 'email.bounced' && !((r.bounce?.type || '').toLowerCase().startsWith('perm')) },
  { id: 'complaint', label: 'Spam complaints', match: (r) => r.event_type === 'email.complained' },
  { id: 'delayed', label: 'Delayed', match: (r) => r.event_type === 'email.delivery_delayed' },
  { id: 'delivered', label: 'Delivered', match: (r) => r.event_type === 'email.delivered' },
];

/**
 * Shows the last 100 bounce/complaint events captured from the Resend
 * webhook. Addresses on rows tagged "Bounce" or "Spam complaint" have
 * already been auto-removed from the newsletter list.
 */
const BouncesPanel = ({ token }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState(true);
  const [filterId, setFilterId] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/newsletter/bounces`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.bounces || []);
    } catch (e) {
      setError(e.message || 'Failed to load bounces.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const activeFilter = FILTERS.find((f) => f.id === filterId) || FILTERS[0];
  const filteredRows = rows.filter(activeFilter.match);

  const purgedCount = rows.filter((r) => r.purged).length;

  return (
    <div
      data-testid="bounces-panel"
      className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-4 sm:p-5 mb-5"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldOff className="h-4 w-4 text-red-400" />
        <p className="text-[10px] font-black tracking-[0.3em] uppercase text-red-400">
          Bounce &amp; complaint log
        </p>
        <span className="text-[10px] text-gray-500 uppercase tracking-widest">
          {rows.length} event{rows.length === 1 ? '' : 's'}
          {purgedCount > 0 && ` · ${purgedCount} auto-purged`}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="bounces-refresh"
            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-white"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            data-testid="bounces-toggle"
            className="text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-white"
          >
            {collapsed ? 'Show' : 'Hide'}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          <p className="text-xs text-gray-500 mt-2 leading-relaxed">
            Resend pings us whenever a story email hard-bounces or is marked as spam. Bounced &amp;
            complained addresses are removed from the newsletter list automatically - this log is
            just an audit trail. Configure the webhook in Resend → Webhooks pointing at
            <code className="mx-1 text-red-300">{`${API}/api/webhooks/resend`}</code>.
          </p>

          {/* Filter chips - click to slice the log by hard/soft bounce, complaint, etc. */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {FILTERS.map((f) => {
              const count = rows.filter(f.match).length;
              const active = f.id === filterId;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilterId(f.id)}
                  data-testid={`bounces-filter-${f.id}`}
                  disabled={count === 0 && f.id !== 'all'}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    active
                      ? 'bg-red-600 border-red-500 text-white'
                      : 'bg-zinc-900 border-zinc-800 text-gray-400 hover:text-white hover:border-zinc-700'
                  }`}
                >
                  {f.label}
                  <span className={`ml-1 rounded px-1 text-[9px] ${active ? 'bg-black/30 text-white' : 'bg-zinc-800 text-gray-500'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {error && (
            <div className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded bg-red-950/40 border border-red-500/50 text-xs">
              <AlertOctagon className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
              <p className="text-red-300">{error}</p>
            </div>
          )}

          {!loading && !error && filteredRows.length === 0 && (
            <p className="text-xs text-gray-500 mt-3 py-4 text-center">
              {rows.length === 0
                ? "No bounces yet - either the webhook isn\u2019t wired up in Resend, or every email so far landed cleanly."
                : `No events match "${activeFilter.label}". Try a different filter.`}
            </p>
          )}

          {filteredRows.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left min-w-[520px]">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="py-2 px-3 text-gray-500 font-bold text-[10px] uppercase tracking-widest">When</th>
                    <th className="py-2 px-3 text-gray-500 font-bold text-[10px] uppercase tracking-widest">Event</th>
                    <th className="py-2 px-3 text-gray-500 font-bold text-[10px] uppercase tracking-widest">Recipient</th>
                    <th className="py-2 px-3 text-gray-500 font-bold text-[10px] uppercase tracking-widest">Detail</th>
                    <th className="py-2 px-3 text-right text-gray-500 font-bold text-[10px] uppercase tracking-widest">Removed</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r, i) => {
                    const badge = BADGES[r.event_type] || { label: r.event_type, tone: 'bg-zinc-800 text-gray-300 border-zinc-700' };
                    const bounceType = r.bounce?.type || r.complaint?.type || '';
                    const bounceMsg = r.bounce?.message || r.complaint?.message || '';
                    return (
                      <tr key={`${r.received_at}-${i}`} className="border-b border-zinc-900/60 hover:bg-zinc-900/40">
                        <td className="py-2 px-3 text-gray-400 text-[11px] whitespace-nowrap">{fmt(r.received_at)}</td>
                        <td className="py-2 px-3">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border ${badge.tone}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-white text-xs break-all">
                          {(r.recipients || []).join(', ') || '-'}
                        </td>
                        <td className="py-2 px-3 text-gray-400 text-[11px]">
                          {bounceType && <span className="font-black text-gray-300 mr-1">{bounceType}</span>}
                          {bounceMsg || r.subject || '-'}
                        </td>
                        <td className="py-2 px-3 text-right text-[11px]">
                          {r.purged
                            ? <span className="text-emerald-400 font-black uppercase tracking-widest">Yes ({r.removed_count || 1})</span>
                            : <span className="text-gray-500">-</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BouncesPanel;
